import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Typography, Link } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';

const Popover = React.lazy(() => import(
  /* webpackChunkName: "popover" */
  '@one-thd/sui-atomic-components/dist/popover/Popover')
  .then(module => ({ default: module.Popover }))
// eslint-disable-next-line no-console
  .catch((error) => console.warn(`Error occurred while loading Popover - ${error}`)));

let deliveryStoreTimer = null;

const WarningPopover = ({
  currentAnchorElt = {},
  storeClick = () => { },
  zipcodeClick = () => { },
}) => {

  const localizerRef = useRef();

  const [popUpMessage, setPopUpMessage] = useState({ open: false, name: null });
  const popupInit = () => {
    if (sessionStorage.getItem('deliveryStoreUpdateFailed')) {
      sessionStorage.removeItem('deliveryStoreUpdateFailed');
      setPopUpMessage({ open: true, name: 'storeFailed' });
      return;
    }

    const validateSoftLinkKey = ({ storageKey, softLink }) => {
      sessionStorage.removeItem(storageKey);
      const deliveryZipDetails = localizerRef.current.getDeliveryZipcodeDetails();
      const zipCode = deliveryZipDetails.zipcode;
      const zipType = deliveryZipDetails.type;
      if (!['AUTO', 'DEFAULT'].includes(zipType) && localizerRef.current) {
        localizerRef.current.validateSoftLink(zipCode, softLink);
      }
    };

    if (sessionStorage.getItem('deliveryzipReloading')) {
      validateSoftLinkKey({ storageKey: 'deliveryzipReloading', softLink: 'selectedZipNotInRange' });
      return;
    }

    if (sessionStorage.getItem('deliveryStoreReloading')) {
      validateSoftLinkKey({ storageKey: 'deliveryStoreReloading', softLink: 'selectedStoreNotInRange' });
    }
  };

  useEffect(() => {
    import('Localizer')
      .then((localizer) => {
        localizerRef.current = localizer;
        popupInit();
        deliveryStoreTimer = setTimeout(() => {
          localizerRef.current.on('selectedZipNotInRange', () => setPopUpMessage({ open: true, name: 'zipCode' }));
          localizerRef.current.on('selectedStoreNotInRange', () => setPopUpMessage({ open: true, name: 'store' }));
        }, 4000);
      })
    // eslint-disable-next-line no-console
      .catch((error) => console.warn(`Error occurred while loading Localizer - ${error}`));
    return () => {
      clearTimeout(deliveryStoreTimer);
    };

  }, []);

  const closeMsgContent = () => {
    if (popUpMessage.open) {
      setPopUpMessage({ open: false, name: null });
      window.removeEventListener('scroll', closeMsgContent);
    }
  };

  const zipCodeMsgContent = () => {
    const storeOnClick = () => { storeClick(); closeMsgContent(); };
    return (
      <>
        <Typography variant="body-xs">
          Looks like this store isn’t near your delivery area.
        </Typography>
        { /* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link onClick={storeOnClick} variant="body-xs">Need to update your store?</Link>
      </>
    );
  };

  const storeMsgContent = () => {
    const zipCodeOnClick = () => { zipcodeClick(); closeMsgContent(); };
    return (
      <>
        <Typography variant="body-xs">
          Looks like this area isn’t near your preferred store.
        </Typography>
        { /* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link onClick={zipCodeOnClick} variant="body-xs">Need to update your ZIP Code?</Link>
      </>
    );
  };

  const storeFailedMsgContent = () => {
    const storeOnClick = () => { storeClick(); closeMsgContent(); };
    return (
      <>
        <Typography variant="body-xs">
          We were unable to update your store.
        </Typography>
        { /* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link onClick={storeOnClick} variant="body-xs">Please update manually.</Link>
      </>
    );
  };

  const popupConfig = {
    zipCode: { render: zipCodeMsgContent, anchorKey: 'deliveryElt', title: 'Store' },
    store: { render: storeMsgContent, anchorKey: 'storeElt', title: 'Zip Code' },
    storeFailed: { render: storeFailedMsgContent, anchorKey: 'deliveryElt', title: 'Store' },
  };

  const { render, anchorKey, title } = popupConfig[popUpMessage.name] || {};
  const showPopMessage = render?.();
  const currentAnchorEltVal = anchorKey ? currentAnchorElt?.[anchorKey] : null;
  const zipCodeStoreTitle = title;

  return (
    <>
      {popUpMessage.open && (
        <Suspense fallback={null}>
          <Popover
            open={popUpMessage.open}
            disablePortal
            id="popover-out-of-range"
            anchorEl={currentAnchorEltVal}
            placement="bottom"
            onClose={closeMsgContent}
            title={zipCodeStoreTitle}
            style={{ zIndex: 1000 }}
          >
            <Typography variant="body-xs">
              {showPopMessage}
            </Typography>
          </Popover>
        </Suspense>
      )}
    </>
  );
};

WarningPopover.propTypes = {
  currentAnchorElt: PropTypes.oneOfType([
    PropTypes.object
  ]),
  storeClick: PropTypes.func,
  zipcodeClick: PropTypes.func
};

WarningPopover.displayName = 'WarningPopover';

export { WarningPopover };